import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import { useMe } from '../lib/session.js';
import { BrandSelect } from '../lib/brands.js';
import { envFromApp } from '../lib/environments.js';
import {
  CHANNEL_LABEL,
  MESSAGE_STATUS_LABEL,
  REASON_LABEL,
  SEND_MODE_LABEL,
  label,
} from '../lib/labels.js';
import { dateTime, num } from '../lib/format.js';
import { hrefFor } from '../lib/router.js';
import {
  Dialog,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Panel,
  StatusBadge,
  Tile,
} from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import { EnvBadge } from '../components/EnvironmentSwitcher.js';
import '../screens-sending.css';

/** Odesílání (Komunikace → Odesílání): odeslané, naplánované, chystané a ověření plánování. Jen čtení. */

type Sub = 'sent' | 'scheduled' | 'preparing' | 'timing';

const SUBS: Array<{ id: Sub; label: string }> = [
  { id: 'sent', label: 'Odeslané' },
  { id: 'scheduled', label: 'Naplánované' },
  { id: 'preparing', label: 'Chystané' },
  { id: 'timing', label: 'Ověření plánování' },
];

export interface SendingLinks {
  onOpenTemplate(templateKey: string): void;
  onOpenBroadcast(id: number): void;
  onOpenPlan(): void;
}

const SOURCE_LABEL: Record<string, string> = {
  journey: 'Cesta zákazníka',
  broadcast: 'Rozesílka',
  api: 'E-shop (transakční)',
  import: 'Import ze SmartEmailingu',
};
const KIND_LABEL: Record<string, string> = {
  broadcast: 'Rozesílka',
  journey: 'Zpráva cesty',
  transactional: 'Transakční z e-shopu',
};
const OUTCOME_LABEL_CS: Record<string, string> = {
  held: 'Zadrženo',
  deferred: 'Odloženo',
  suppressed: 'Nepošle se',
  merged: 'Sloučeno',
  expired: 'Propadlo',
  wait: 'Čeká',
  awaiting_step: 'Čeká na potvrzení kroku',
};
const EXTRA_REASON: Record<string, string> = {
  availability_none: 'čeká na dostupnou vstupenku k závodu',
  template_changed: 'šablona se po schválení změnila',
  awaiting_step: 'čeká na potvrzení dalšího kroku rozesílky',
};
const reasonLabel = (reason: string | null): string =>
  reason ? (EXTRA_REASON[reason] ?? REASON_LABEL[reason] ?? reason) : '';

const hm = (iso: string, zone: string): string => dateTime(iso, zone);

export function Sending({ links }: { links: SendingLinks }): JSX.Element {
  const me = useMe().data;
  const [sub, setSub] = useState<Sub>('scheduled');
  const env = me?.environment?.name ?? envFromApp(me?.appEnv ?? 'development');
  return (
    <>
      <PageHeader
        level={2}
        title="Odesílání"
        description="Co hub odeslal, co a kdy odešle a co se teprve chystá. Nic z toho tu nic neodesílá ani nepřepíná, jen ukazuje."
        actions={
          <span className={`env-${env} snd-env`} data-testid="sending-env">
            <EnvBadge name={env} />
            {me ? (
              <span className="snd-mode">Odesílání: {label(SEND_MODE_LABEL, me.sendMode)}</span>
            ) : null}
          </span>
        }
      />
      <div className="seg" role="tablist" aria-label="Odesílání">
        {SUBS.map((s) => (
          <button
            key={s.id}
            role="tab"
            aria-selected={sub === s.id}
            className={sub === s.id ? 'on' : ''}
            onClick={() => setSub(s.id)}
            data-testid={`sending-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {sub === 'sent' ? <SentView links={links} /> : null}
      {sub === 'scheduled' ? <ScheduledView links={links} /> : null}
      {sub === 'preparing' ? <PreparingView links={links} /> : null}
      {sub === 'timing' ? <TimingView /> : null}
    </>
  );
}

/* ------------------------------------------------------------------ odeslané --- */

interface SentRow {
  id: string;
  at: string;
  kind: 'service' | 'marketing';
  source: string;
  code: string | null;
  broadcastId: number | null;
  brand: string | null;
  language: string | null;
  templateKey: string;
  status: string;
  mode: string;
  contactId: string;
  recipient: string;
  raceId: string | null;
  raceName: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  webVisitAt: string | null;
  webActive: boolean;
  bouncedAt: string | null;
  unsubscribedAt: string | null;
}
interface SentResponse {
  page: number;
  pageSize: number;
  total: number;
  sums: Record<
    'total' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'failed' | 'unsubscribed' | 'dryRun',
    number
  >;
  rates: Record<'delivered' | 'opened' | 'clicked' | 'bounced', number>;
  items: SentRow[];
}

const dayInput = (d: Date): string => d.toISOString().slice(0, 10);

function SentView({ links }: { links: SendingLinks }): JSX.Element {
  const me = useMe().data;
  const now = me ? new Date(me.now) : new Date();
  const [from, setFrom] = useState(dayInput(new Date(now.getTime() - 30 * 86_400_000)));
  const [to, setTo] = useState(dayInput(new Date(now.getTime() + 86_400_000)));
  const [kind, setKind] = useState('');
  const [source, setSource] = useState('');
  const [state, setState] = useState('');
  const [code, setCode] = useState('');
  const [brand, setBrand] = useState('');
  const [language, setLanguage] = useState('');
  const [recipient, setRecipient] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);

  const reset =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(1);
    };
  const search = recipient.trim().length >= 3 ? recipient.trim() : '';

  const query = useQuery<SentResponse>({
    queryKey: ['sending-sent', from, to, kind, source, state, code, brand, language, search, page],
    queryFn: () =>
      api.get(
        `/api/sending/sent${qs({
          from: from ? new Date(`${from}T00:00:00`).toISOString() : '',
          to: to ? new Date(`${to}T00:00:00`).toISOString() : '',
          kind,
          source,
          state,
          code: code.trim(),
          brand,
          language: language.trim(),
          recipient: search,
          page,
          pageSize: 25,
        })}`,
      ),
  });
  const data = query.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <Panel title="Filtr">
        <div className="row snd-filters">
          <label>
            Od
            <input type="date" value={from} onChange={(e) => reset(setFrom)(e.target.value)} />
          </label>
          <label>
            Do (bez tohoto dne)
            <input type="date" value={to} onChange={(e) => reset(setTo)(e.target.value)} />
          </label>
          <label>
            Druh
            <select value={kind} onChange={(e) => reset(setKind)(e.target.value)}>
              <option value="">Servis i marketing</option>
              <option value="service">Servis</option>
              <option value="marketing">Marketing</option>
            </select>
          </label>
          <label>
            Zdroj
            <select value={source} onChange={(e) => reset(setSource)(e.target.value)}>
              <option value="">Všechny zdroje</option>
              {Object.entries(SOURCE_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label>
            Stav
            <select value={state} onChange={(e) => reset(setState)(e.target.value)}>
              <option value="">Jakýkoli</option>
              <option value="delivered">Doručeno</option>
              <option value="opened">Otevřeno</option>
              <option value="clicked">Kliknuto</option>
              <option value="bounced">Odskočilo</option>
              <option value="unsubscribed">Odhlášení</option>
              <option value="failed">Chyba odeslání</option>
              <option value="dry_run">Nanečisto</option>
            </select>
          </label>
          <label>
            Značka
            <BrandSelect value={brand} onChange={reset(setBrand)} />
          </label>
          <label>
            Jazyk
            <input
              value={language}
              placeholder="např. cs"
              className="snd-w80"
              onChange={(e) => reset(setLanguage)(e.target.value)}
            />
          </label>
          <label>
            Kód zprávy
            <input
              value={code}
              placeholder="M02, BC12 nebo BC"
              className="snd-w120"
              onChange={(e) => reset(setCode)(e.target.value)}
            />
          </label>
          <label>
            Příjemce
            <input
              type="search"
              value={recipient}
              placeholder="začátek adresy (3 znaky)"
              onChange={(e) => reset(setRecipient)(e.target.value)}
              data-testid="sending-recipient"
            />
          </label>
        </div>
        <p className="note">
          Adresy příjemců jsou schované (první písmeno a doména). Hledat jde podle začátku adresy.
        </p>
      </Panel>

      {query.isLoading ? <Loading what="odeslané zprávy" /> : null}
      {query.isError ? (
        <ErrorState
          title="Odeslané zprávy se nepodařilo načíst"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {data ? (
        <div className="tiles snd-tiles">
          <Tile label="Zpráv ve filtru" value={num(data.sums.total)} />
          <Tile
            label="Doručeno"
            value={num(data.sums.delivered)}
            note={`${data.rates.delivered} % z odeslaných`}
          />
          <Tile label="Otevřeno" value={num(data.sums.opened)} note={`${data.rates.opened} %`} />
          <Tile label="Kliknuto" value={num(data.sums.clicked)} note={`${data.rates.clicked} %`} />
          <Tile
            label="Odskočilo"
            value={num(data.sums.bounced)}
            note={`${data.rates.bounced} %`}
            bad={data.sums.bounced > 0}
          />
          <Tile label="Chyba odeslání" value={num(data.sums.failed)} bad={data.sums.failed > 0} />
          <Tile label="Nanečisto" value={num(data.sums.dryRun)} note="nikam neodešlo" />
        </div>
      ) : null}

      {data ? (
        <Panel title={`Zprávy (${num(data.total)})`}>
          {data.items.length === 0 ? (
            <EmptyState title="V tomto výběru nic neodešlo">
              Zkuste delší období, jiný druh zpráv nebo smažte některý filtr.
            </EmptyState>
          ) : (
            <div className="scroll">
              <table data-testid="sending-sent-table">
                <thead>
                  <tr>
                    <th>Kdy</th>
                    <th>Kód</th>
                    <th>Zdroj</th>
                    <th>Značka</th>
                    <th>Jazyk</th>
                    <th>Příjemce</th>
                    <th>Stav</th>
                    <th>Reakce</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((m) => (
                    <tr key={m.id}>
                      <td>{hm(m.at, me?.defaultTimezone ?? 'Europe/Prague')}</td>
                      <td>
                        <code>{m.code ?? '—'}</code>{' '}
                        <small className="muted">{label(CHANNEL_LABEL, m.kind)}</small>
                        {m.raceName ? (
                          <>
                            <br />
                            <small className="muted">závod: {m.raceName}</small>
                          </>
                        ) : null}
                      </td>
                      <td>{SOURCE_LABEL[m.source] ?? m.source}</td>
                      <td>{m.brand ?? '—'}</td>
                      <td>{m.language ?? '—'}</td>
                      <td>
                        <a href={hrefFor('customers', m.contactId)}>{m.recipient}</a>
                      </td>
                      <td>{label(MESSAGE_STATUS_LABEL, m.status)}</td>
                      <td>
                        <Reactions m={m} />
                      </td>
                      <td>
                        <button className="btn" onClick={() => setOpen(m.id)}>
                          Detail
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="row s2b-pager">
            <button className="btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              ← Novější
            </button>
            <span className="note">
              Strana {page} z {pages}
            </span>
            <button className="btn" disabled={page >= pages} onClick={() => setPage(page + 1)}>
              Starší →
            </button>
          </div>
        </Panel>
      ) : null}

      {open ? <SentDetail id={open} links={links} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function Reactions({ m }: { m: SentRow }): JSX.Element {
  return (
    <>
      {m.deliveredAt ? <StatusBadge tone="ok">doručeno</StatusBadge> : null}
      {m.openedAt ? <StatusBadge tone="ok">otevřeno</StatusBadge> : null}
      {m.clickedAt ? <StatusBadge tone="acc">proklik</StatusBadge> : null}
      {m.webVisitAt ? (
        <span title={`Návštěva webu po e-mailu: ${dateTime(m.webVisitAt)}`}>
          <StatusBadge tone="acc">{m.webActive ? 'na webu (aktivní)' : 'na webu'}</StatusBadge>
        </span>
      ) : null}
      {m.unsubscribedAt ? <StatusBadge tone="warn">odhlášení</StatusBadge> : null}
      {m.bouncedAt ? <StatusBadge tone="bad">odskočilo</StatusBadge> : null}
      {m.status === 'failed' ? <StatusBadge tone="bad">chyba</StatusBadge> : null}
      {m.status === 'dry_run' ? <StatusBadge tone="sim">nanečisto</StatusBadge> : null}
    </>
  );
}

interface SentDetailData {
  id: string;
  at: string;
  kind: string;
  source: string;
  status: string;
  brand: string | null;
  language: string | null;
  recipient: string;
  contactId: string;
  items: Array<{ code: string; occurrenceKey: string }>;
  timeline: Array<{ key: string; label: string; at: string }>;
  reason: string | null;
  template: {
    key: string;
    name: string | null;
    exists: boolean;
    version: number | null;
    versionStatus: string | null;
    versionBy: 'hash' | 'time' | null;
    publishedVersion: number | null;
    lastEditedBy: string | null;
    lastEditedAt: string | null;
  };
  broadcast: { id: number; name: string; status: string } | null;
  web: Array<{
    site: string;
    startedAt: string;
    lastSeenAt: string;
    landingPath: string | null;
    referrerHost: string | null;
    device: string | null;
    attribution: 'exact' | 'probable' | null;
    pageviews: number;
    engagedSeconds: number;
    maxScrollPct: number;
    keyEvents: number;
    active: boolean;
    events: Array<{
      type: string;
      at: string;
      path: string | null;
      name: string | null;
      raceId: string | null;
    }>;
  }>;
}

const WEB_EVENT_LABEL: Record<string, string> = {
  page_view: 'zobrazil stránku',
  cart: 'práce s košíkem',
  flexi_view: 'zobrazil Flexi',
  order_status_view: 'zobrazil stav objednávky',
  checkout_start: 'začal objednávku',
  price_view: 'zobrazil ceny',
  support_open: 'otevřel podporu',
  support_link_click: 'klikl na podporu',
  language_change: 'změnil jazyk',
  content_read: 'četl obsah',
  key_event: 'klíčová akce',
  identify: 'přihlášení / identifikace',
};

function SentDetail({
  id,
  links,
  onClose,
}: {
  id: string;
  links: SendingLinks;
  onClose: () => void;
}): JSX.Element {
  const q = useQuery<SentDetailData>({
    queryKey: ['sending-sent-detail', id],
    queryFn: () => api.get(`/api/sending/sent/${id}`),
  });
  const d = q.data;
  return (
    <Dialog open title="Detail zprávy" onClose={onClose} wide>
      {q.isLoading ? <Loading what="detail zprávy" /> : null}
      {q.isError ? <ErrorState title="Detail se nepodařilo načíst" error={q.error} /> : null}
      {d ? (
        <div data-testid="sending-detail">
          <dl className="snd-dl">
            <dt>Příjemce</dt>
            <dd>
              <a href={hrefFor('customers', d.contactId)}>{d.recipient}</a> (karta kontaktu)
            </dd>
            <dt>Zpráva</dt>
            <dd>
              {d.items.map((i) => i.code).join(', ') || '—'} · {label(CHANNEL_LABEL, d.kind)} ·{' '}
              {SOURCE_LABEL[d.source] ?? d.source} · značka {d.brand ?? '—'} · jazyk{' '}
              {d.language ?? '—'}
            </dd>
            <dt>Stav</dt>
            <dd>
              {label(MESSAGE_STATUS_LABEL, d.status)}
              {d.reason ? <span className="snd-reason"> · {d.reason}</span> : null}
            </dd>
            <dt>Šablona</dt>
            <dd>
              <code>{d.template.key}</code>
              {d.template.version
                ? ` · verze ${d.template.version}${d.template.versionBy === 'time' ? ' (podle času odeslání)' : ''}`
                : ' · verzi nelze určit'}
              {d.template.publishedVersion
                ? ` (dnes publikovaná verze ${d.template.publishedVersion})`
                : ''}
              <br />
              {d.template.lastEditedBy
                ? `Naposledy upravil(a) ${d.template.lastEditedBy}, ${hm(d.template.lastEditedAt ?? d.at, 'Europe/Prague')}`
                : 'Autor úpravy není znám.'}
            </dd>
          </dl>
          <h3 className="snd-h3">Časová osa</h3>
          <ol className="snd-timeline">
            {d.timeline.map((t) => (
              <li key={t.key}>
                <b>{t.label}</b> <span className="muted">{hm(t.at, 'Europe/Prague')}</span>
              </li>
            ))}
          </ol>
          {d.web.length > 0 ? (
            <>
              <h3 className="snd-h3">Návštěva webu po e-mailu</h3>
              {d.web.map((v) => (
                <div key={v.startedAt} data-testid="sending-detail-web">
                  <p>
                    <b>{v.site}</b> · {hm(v.startedAt, 'Europe/Prague')} · vstup na{' '}
                    <code>{v.landingPath ?? '—'}</code>
                    {v.referrerHost ? ` · z ${v.referrerHost}` : ''}
                    {v.device ? ` · ${v.device}` : ''}
                    <br />
                    <span className="muted">
                      {v.attribution === 'exact' ? 'přesně podle odkazu' : 'pravděpodobně'} ·{' '}
                      {v.pageviews} {v.pageviews === 1 ? 'stránka' : 'stránek'} · aktivní{' '}
                      {v.engagedSeconds} s · posun do {v.maxScrollPct} % · klíčových akcí{' '}
                      {v.keyEvents}
                      {v.active ? ' · aktivní návštěva' : ''}
                    </span>
                  </p>
                  {v.events.length > 0 ? (
                    <ol className="snd-timeline">
                      {v.events.map((e) => (
                        <li key={`${e.at}-${e.type}-${e.path ?? e.name ?? ''}`}>
                          <b>{WEB_EVENT_LABEL[e.type] ?? e.type}</b>
                          {e.path ? <code> {e.path}</code> : null}
                          {e.name ? ` (${e.name})` : ''}
                          {e.raceId ? ` · závod ${e.raceId}` : ''}{' '}
                          <span className="muted">{hm(e.at, 'Europe/Prague')}</span>
                        </li>
                      ))}
                    </ol>
                  ) : null}
                </div>
              ))}
            </>
          ) : null}
          <div className="row snd-actions">
            {d.template.exists ? (
              <button className="btn" onClick={() => links.onOpenTemplate(d.template.key)}>
                Upravit šablonu
              </button>
            ) : null}
            {d.broadcast ? (
              <button className="btn" onClick={() => links.onOpenBroadcast(d.broadcast!.id)}>
                Otevřít rozesílku {d.broadcast.name}
              </button>
            ) : null}
            <a className="btn" href={hrefFor('customers', d.contactId)}>
              Karta kontaktu
            </a>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}

/* --------------------------------------------------------------- naplánované --- */

interface UpcomingItem {
  kind: 'broadcast' | 'journey' | 'transactional';
  code: string;
  title: string;
  brand: string | null;
  at: string;
  lastAt: string;
  zone: string;
  recipients: number;
  state: 'send' | 'held';
  outcome: string | null;
  reason: string | null;
  reasonText: string | null;
  why: string;
  dryRun: boolean;
  broadcastId: number | null;
  templateKey: string | null;
}
interface UpcomingResponse {
  now: string;
  from: string;
  to: string;
  zone: string;
  sendMode: string;
  dryRun: boolean;
  truncated: boolean;
  journeyContacts: number;
  items: UpcomingItem[];
  totals: { send: number; held: number };
  hours?: Array<{
    at: string;
    local: string;
    send: number;
    held: number;
    items: Array<{
      kind: string;
      code: string;
      title: string;
      brand: string | null;
      send: number;
      held: number;
      reasons: string[];
    }>;
  }>;
  note?: string;
}

function ScheduledView({ links }: { links: SendingLinks }): JSX.Element {
  const [mode, setMode] = useState<'list' | 'hours'>('list');
  const [days, setDays] = useState(7);
  const [kind, setKind] = useState('');
  const [brand, setBrand] = useState('');
  const me = useMe().data;

  const query = useQuery<UpcomingResponse>({
    queryKey: ['sending-scheduled', mode, days, kind, brand],
    queryFn: () => {
      if (mode === 'hours') {
        const from = me ? new Date(me.now) : new Date();
        const to = new Date(from.getTime() + Math.min(days, 7) * 86_400_000);
        return api.get(
          `/api/sending/preview${qs({ from: from.toISOString(), to: to.toISOString(), brand })}`,
        );
      }
      return api.get(`/api/sending/scheduled${qs({ days, kind, brand })}`);
    },
  });
  const data = query.data;

  return (
    <>
      <Panel title="Co se bude posílat">
        <div className="row snd-filters">
          <div className="seg" role="tablist" aria-label="Zobrazení">
            <button
              role="tab"
              aria-selected={mode === 'list'}
              className={mode === 'list' ? 'on' : ''}
              onClick={() => setMode('list')}
            >
              Seznam
            </button>
            <button
              role="tab"
              aria-selected={mode === 'hours'}
              className={mode === 'hours' ? 'on' : ''}
              onClick={() => setMode('hours')}
              data-testid="sending-hours"
            >
              Co se odešle po hodinách
            </button>
          </div>
          <label>
            Období
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {[1, 2, 3, 7, 14, 30].map((d) => (
                <option key={d} value={d} disabled={mode === 'hours' && d > 7}>
                  {d === 1 ? 'Příštích 24 hodin' : `Příštích ${d} dní`}
                </option>
              ))}
            </select>
          </label>
          {mode === 'list' ? (
            <label>
              Druh
              <select value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="">Všechny druhy</option>
                {Object.entries(KIND_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label>
            Značka
            <BrandSelect value={brand} onChange={setBrand} />
          </label>
        </div>
        <p className="note">
          Zprávy cest počítá stejný plánovač a stejná pravidla jako ostrý provoz, rozesílky mají
          čas, který jim někdo nastavil. Tady se nic neodesílá.
        </p>
      </Panel>

      {query.isLoading ? <Loading what="naplánované odeslání" /> : null}
      {query.isError ? (
        <ErrorState
          title="Naplánované odeslání se nepodařilo načíst"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {data ? (
        <>
          {data.dryRun ? (
            <p className="snd-banner sim" role="status">
              Režim odesílání je Nanečisto: nic z níže uvedeného skutečně neodejde ven. Tabulka
              ukazuje, co by se odeslalo.
            </p>
          ) : null}
          <div className="tiles snd-tiles">
            <Tile label="Odešle se" value={num(data.totals.send)} note="zpráv v tomto okně" />
            <Tile
              label="Zadrženo, odloženo, nepošle se"
              value={num(data.totals.held)}
              bad={data.totals.held > 0}
              note="důvod je u každé řádky"
            />
            <Tile
              label="Okno"
              value={`${hm(data.from, data.zone)}`}
              note={`do ${hm(data.to, data.zone)}`}
            />
          </div>
          {data.truncated ? (
            <p className="snd-banner warn" role="status">
              Kontaktů je příliš mnoho (nebo výpočet trval dlouho), vyhodnotilo se prvních{' '}
              {num(data.journeyContacts)}, a to napřed ty s chystanou zprávou. Čísla zpráv cest jsou
              proto spodní odhad.
            </p>
          ) : null}
          {mode === 'hours' ? (
            <HoursTable data={data} />
          ) : (
            <UpcomingTable data={data} links={links} />
          )}
          {data.note && mode === 'hours' ? <p className="note">{data.note}</p> : null}
        </>
      ) : null}
    </>
  );
}

function stateBadge(i: UpcomingItem): JSX.Element {
  if (i.state === 'send')
    return <StatusBadge tone={i.dryRun ? 'sim' : 'ok'}>Odešle se</StatusBadge>;
  const tone: Tone = i.outcome === 'suppressed' || i.outcome === 'expired' ? 'neu' : 'warn';
  return (
    <StatusBadge tone={tone}>{OUTCOME_LABEL_CS[i.outcome ?? 'held'] ?? 'Zadrženo'}</StatusBadge>
  );
}

function UpcomingTable({
  data,
  links,
}: {
  data: UpcomingResponse;
  links: SendingLinks;
}): JSX.Element {
  if (data.items.length === 0)
    return (
      <Panel title="Naplánované">
        <EmptyState title="V tomto období se nic neodesílá">
          Nic není naplánované a plánovač zatím nevidí žádnou zprávu, která by v tomto období měla
          odejít. Zkuste delší období.
        </EmptyState>
      </Panel>
    );
  return (
    <Panel title={`Naplánované (${data.items.length})`}>
      <div className="scroll">
        <table data-testid="sending-scheduled-table">
          <thead>
            <tr>
              <th>Kdy</th>
              <th>Druh</th>
              <th>Zpráva</th>
              <th>Značka</th>
              <th className="n">Příjemců</th>
              <th>Stav</th>
              <th>Proč</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.items.map((i, idx) => (
              <tr key={`${i.kind}-${i.code}-${i.at}-${idx}`}>
                <td>
                  {hm(i.at, i.zone)}
                  {i.lastAt.slice(0, 13) !== i.at.slice(0, 13) ? (
                    <small className="muted"> až {hm(i.lastAt, i.zone)}</small>
                  ) : null}
                </td>
                <td>{KIND_LABEL[i.kind]}</td>
                <td>
                  <code>{i.code}</code> {i.title}
                </td>
                <td>{i.brand ?? '—'}</td>
                <td className="n">{num(i.recipients)}</td>
                <td>
                  {stateBadge(i)}
                  {i.reason ? <div className="snd-reason">{reasonLabel(i.reason)}</div> : null}
                </td>
                <td className="snd-why">{i.why}</td>
                <td className="snd-links">
                  {i.broadcastId ? (
                    <button className="btn" onClick={() => links.onOpenBroadcast(i.broadcastId!)}>
                      Otevřít rozesílku
                    </button>
                  ) : null}
                  {i.templateKey ? (
                    <button className="btn" onClick={() => links.onOpenTemplate(i.templateKey!)}>
                      Upravit šablonu
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function HoursTable({ data }: { data: UpcomingResponse }): JSX.Element {
  const hours = data.hours ?? [];
  if (hours.length === 0)
    return (
      <Panel title="Po hodinách">
        <EmptyState title="V tomto okně se nic neodešle">
          Plánovač ani rozesílky v něm nic nemají. Zkuste delší okno.
        </EmptyState>
      </Panel>
    );
  return (
    <Panel title="Po hodinách" subtitle={`Čas v pásmu ${data.zone}`}>
      <div className="scroll">
        <table data-testid="sending-hours-table">
          <thead>
            <tr>
              <th>Hodina</th>
              <th className="n">Odešle se</th>
              <th className="n">Zadrženo</th>
              <th>Co</th>
            </tr>
          </thead>
          <tbody>
            {hours.map((h) => (
              <tr key={h.at}>
                <td>{h.local}</td>
                <td className="n">{num(h.send)}</td>
                <td className="n">{num(h.held)}</td>
                <td>
                  {h.items.map((i) => (
                    <div key={`${i.kind}${i.code}${i.brand}`}>
                      <code>{i.code}</code> {i.title}: {num(i.send)} se odešle
                      {i.held > 0
                        ? `, ${num(i.held)} zadrženo (${i.reasons.map(reasonLabel).join('; ')})`
                        : ''}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ chystané --- */

interface PreparingItem {
  kind: 'broadcast_draft' | 'plan_item' | 'template_draft';
  id: string;
  title: string;
  brand: string | null;
  status: string;
  missing: Array<{ code: string; text: string }>;
  readiness: 'ready' | 'incomplete';
  broadcastId: number | null;
  planItemId: number | null;
  templateKey: string | null;
  due: string | null;
  editedBy: string | null;
  editedAt: string | null;
}

function PreparingView({ links }: { links: SendingLinks }): JSX.Element {
  const [brand, setBrand] = useState('');
  const query = useQuery<{ items: PreparingItem[]; counts: Record<string, number> }>({
    queryKey: ['sending-preparing', brand],
    queryFn: () => api.get(`/api/sending/preparing${qs({ brand })}`),
  });
  const data = query.data;
  const groups: Array<[PreparingItem['kind'], string]> = [
    ['broadcast_draft', 'Koncepty rozesílek'],
    ['plan_item', 'Položky plánu rozesílek bez rozesílky'],
    ['template_draft', 'Šablony v konceptu'],
  ];
  return (
    <>
      <Panel title="Filtr">
        <div className="row snd-filters">
          <label>
            Značka
            <BrandSelect value={brand} onChange={setBrand} />
          </label>
        </div>
      </Panel>
      {query.isLoading ? <Loading what="chystané odesílání" /> : null}
      {query.isError ? (
        <ErrorState
          title="Chystané odesílání se nepodařilo načíst"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}
      {data
        ? groups.map(([kind, title]) => {
            const items = data.items.filter((i) => i.kind === kind);
            return (
              <Panel key={kind} title={`${title} (${items.length})`}>
                {items.length === 0 ? (
                  <EmptyState title="Nic se tu nechystá">
                    Všechno z tohoto druhu je hotové.
                  </EmptyState>
                ) : (
                  <div className="scroll">
                    <table data-testid={`sending-preparing-${kind}`}>
                      <thead>
                        <tr>
                          <th>Název</th>
                          <th>Značka</th>
                          <th>Stav</th>
                          <th>Co chybí k odeslání</th>
                          <th>Naposledy upravil(a)</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((i) => (
                          <tr key={`${i.kind}-${i.id}`}>
                            <td>
                              {i.title}
                              {i.due ? <small className="muted"> · týden od {i.due}</small> : null}
                            </td>
                            <td>{i.brand ?? '—'}</td>
                            <td>{i.status}</td>
                            <td>
                              <ul className="snd-missing">
                                {i.missing.map((m) => (
                                  <li key={m.code}>
                                    <StatusBadge tone="warn">chybí</StatusBadge> {m.text}
                                  </li>
                                ))}
                              </ul>
                            </td>
                            <td>
                              {i.editedBy ?? '—'}
                              {i.editedAt ? (
                                <small className="muted">
                                  {' '}
                                  · {hm(i.editedAt, 'Europe/Prague')}
                                </small>
                              ) : null}
                            </td>
                            <td className="snd-links">
                              {i.broadcastId ? (
                                <button
                                  className="btn pri"
                                  onClick={() => links.onOpenBroadcast(i.broadcastId!)}
                                >
                                  Dokončit rozesílku
                                </button>
                              ) : null}
                              {i.planItemId ? (
                                <button className="btn" onClick={() => links.onOpenPlan()}>
                                  Otevřít plán rozesílek
                                </button>
                              ) : null}
                              {i.templateKey ? (
                                <button
                                  className="btn"
                                  onClick={() => links.onOpenTemplate(i.templateKey!)}
                                >
                                  Upravit šablonu
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            );
          })
        : null}
    </>
  );
}

/* ---------------------------------------------------------- ověření plánování --- */

interface TimingResponse {
  now: string;
  nowLocal: string;
  zone: string;
  clockMode: string;
  cronTimezone: string;
  cronNote: string;
  worker: { lastSeenAt: string | null; secondsAgo: number } | null;
  jobs: Array<{
    name: string;
    label: string;
    cron: string;
    paused: boolean;
    nextRunAt: string;
    nextRunLocal: string;
    lastRun: {
      startedAt: string;
      status: string;
      trigger: string;
      slot: string;
      deviationSeconds: number | null;
    } | null;
    recentDeviations: number[];
    maxDeviationSeconds: number | null;
  }>;
  broadcasts: Array<{
    id: number;
    code: string;
    name: string;
    brand: string;
    status: string;
    scheduledAt: string | null;
    scheduledLocal: string | null;
    startedAt: string | null;
    deviationSeconds: number | null;
  }>;
  special: Array<{
    key: string;
    label: string;
    rule: string;
    nextAt: string | null;
    nextLocal: string | null;
    lastAt: string | null;
    note: string;
  }>;
}

const secs = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : `${num(n)} s`;

function TimingView(): JSX.Element {
  const query = useQuery<TimingResponse>({
    queryKey: ['sending-timing'],
    queryFn: () => api.get('/api/sending/timing'),
    refetchInterval: 15_000,
  });
  const data = query.data;
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  return (
    <>
      <Panel title="Jak se plánování ověřuje">
        <p>
          Tady vidíte, kdy se příště spustí každá úloha na pozadí, kdy se naposledy opravdu spustila
          a o kolik sekund později, než měla. U rozesílek je vedle sebe čas, na který byly
          naplánované, a čas, kdy opravdu začaly. Zkušební rozesílku na interní seznam můžete
          naplánovat na čas T a po něm tu ověřit, že se spustila v tu chvíli (postup je v dokumentu
          docs/OVERENI-CASOVANI.md).
        </p>
      </Panel>
      {query.isLoading ? <Loading what="ověření plánování" /> : null}
      {query.isError ? (
        <ErrorState
          title="Přehled plánování se nepodařilo načíst"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}
      {data ? (
        <>
          <div className="tiles snd-tiles">
            <Tile
              label="Teď (pásmo hubu)"
              value={data.nowLocal}
              note={data.clockMode === 'simulated' ? 'simulovaný čas' : 'skutečný čas'}
            />
            <Tile
              label="Worker se ozval"
              value={data.worker ? `před ${num(data.worker.secondsAgo)} s` : 'nikdy'}
              bad={!data.worker || data.worker.secondsAgo > 600}
              note={data.clockMode === 'simulated' ? 've Vývoji se neměří' : undefined}
            />
            <Tile label="Rozvrh úloh" value={data.cronTimezone} note="cron běží v UTC" />
          </div>
          <p className="note">{data.cronNote}</p>

          <Panel title="Příští spuštění úloh">
            <div className="scroll">
              <table data-testid="sending-timing-jobs">
                <thead>
                  <tr>
                    <th>Úloha</th>
                    <th>Rozvrh (cron, UTC)</th>
                    <th>Příští spuštění</th>
                    <th>Poslední spuštění</th>
                    <th className="n">Odchylka</th>
                    <th className="n">Největší z posledních</th>
                  </tr>
                </thead>
                <tbody>
                  {data.jobs.map((j) => (
                    <tr key={j.name}>
                      <td>
                        {j.label} <code>{j.name}</code>
                        {j.paused ? <StatusBadge tone="warn">pozastaveno</StatusBadge> : null}
                      </td>
                      <td>
                        <code>{j.cron}</code>
                      </td>
                      <td>{j.nextRunLocal}</td>
                      <td>
                        {j.lastRun ? (
                          <>
                            {hm(j.lastRun.startedAt, data.zone)}{' '}
                            <StatusBadge tone={j.lastRun.status === 'ok' ? 'ok' : 'bad'}>
                              {j.lastRun.status === 'ok' ? 'v pořádku' : 'selhalo'}
                            </StatusBadge>
                            {j.lastRun.trigger === 'manual' ? (
                              <small className="muted"> ručně</small>
                            ) : null}
                          </>
                        ) : (
                          <span className="muted">dosud neběžela</span>
                        )}
                      </td>
                      <td className="n">{secs(j.lastRun?.deviationSeconds)}</td>
                      <td className="n">{secs(j.maxDeviationSeconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel title="Rozesílky: naplánováno a skutečně spuštěno">
            {data.broadcasts.length === 0 ? (
              <EmptyState title="Žádná rozesílka s naplánovaným časem">
                Naplánujte zkušební rozesílku na interní seznam a vraťte se sem.
              </EmptyState>
            ) : (
              <div className="scroll">
                <table data-testid="sending-timing-broadcasts">
                  <thead>
                    <tr>
                      <th>Rozesílka</th>
                      <th>Stav</th>
                      <th>Naplánováno na</th>
                      <th>Skutečně spuštěno</th>
                      <th className="n">Odchylka</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.broadcasts.map((b) => (
                      <tr key={b.id}>
                        <td>
                          <code>{b.code}</code> {b.name}
                        </td>
                        <td>{b.status}</td>
                        <td>{b.scheduledLocal ?? '—'}</td>
                        <td>{b.startedAt ? hm(b.startedAt, data.zone) : 'ještě ne'}</td>
                        <td className="n">{secs(b.deviationSeconds)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title="Úlohy, které rozhoduje místní čas">
            <ul className="snd-missing">
              {data.special.map((s) => (
                <li key={s.key}>
                  <b>{s.label}</b>: {s.rule} Příště: {s.nextLocal ?? 'vypnuto'}. Naposledy:{' '}
                  {s.lastAt ? hm(s.lastAt, data.zone) : 'dosud neodešel'}.{' '}
                  <span className="muted">{s.note}</span>
                </li>
              ))}
            </ul>
          </Panel>
        </>
      ) : null}
    </>
  );
}
